/**
 * **What happened to one calendar occurrence** — the one rule, read off `status` and
 * `derivedStatus` together.
 *
 * Promoted out of `pages/client-calendar/model/clientEvent` on 18 September 2026 on its second
 * consumer: `/zadania` draws a client's week beside a missed workout, and it has to tell a session
 * that happened from one that did not in exactly the words the calendar does. Reimplementing the
 * precedence would have been two answers to one question, and the two would eventually disagree
 * about a cancelled workout — whose `derivedStatus` is `null`.
 *
 * Nothing else came with it. The card treatment, the tone, the glyph, the title and the kind stay
 * in the calendar slice, because they are drawings rather than facts.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 *
 * ── The precedence, and why it is in this order (R14, R21) ───────────────────────────────────
 * The recorded outcome settles first: `derivedStatus` is `null` for a voided workout, so reading it
 * first would leave a cancellation stateless. Then the classifier's verdict. Then, for a
 * consultation — which the classifier never runs on (R2) — the raw status is all there is.
 *
 * ── `null` is an ANSWER, not a failure ───────────────────────────────────────────────────────
 * A word this build has never seen answers `null` and the caller says so. Folding it onto
 * `scheduled` is the one genuinely dangerous outcome: it would tell the trainer a session has not
 * happened yet when the server may be saying the opposite.
 */

/** The status word. Nine, and the calendar's own `calendar.status.*` names them. */
export type OccurrenceStatusKey =
  | 'completed'
  | 'live'
  | 'missed'
  | 'noShow'
  | 'scheduled'
  | 'cancelled'
  | 'lateCancelled'
  | 'skipped'
  | 'emptySlot';

/** The two wire fields the rule reads, and nothing else. */
export interface OccurrenceStatusSource {
  /** The stored status. Authoritative for Cancelled, LateCancelled, NoShow and consultations. */
  readonly status: string | null;
  /** R14's verdict on a workout, or `null` for a consultation and for a voided workout. */
  readonly derivedStatus: string | null;
}

/**
 * R14's classifier vocabulary, as `CalendarBindingProjector` spells it.
 *
 * All seven members of `CalendarOccurrenceStatus`, and it has to stay all seven: a member missing
 * here is not a blank status, it is the server's raw English word printed on a Polish calendar.
 */
const BY_DERIVED: Record<string, OccurrenceStatusKey> = {
  Done: 'completed',
  InProgress: 'live',
  Missed: 'missed',
  NoShow: 'noShow',
  Skipped: 'skipped',
  EmptySlot: 'emptySlot',
  Upcoming: 'scheduled',
};

/**
 * `CalendarEventStatus`, as the enum spells it — all seven members, for the reason above.
 *
 * The three voided ones settle before anything. `Skipped` does not need to: the backend's own
 * classifier tests it ahead of every other branch, so a declined occurrence's `derivedStatus` says
 * `Skipped` too and the derived path reaches the same answer. It is mapped here anyway, because a
 * row that arrives with the raw status and no derived one must not fall out of the table.
 */
const BY_STATUS: Record<string, OccurrenceStatusKey> = {
  Cancelled: 'cancelled',
  LateCancelled: 'lateCancelled',
  NoShow: 'noShow',
  Skipped: 'skipped',
  Completed: 'completed',
  InProgress: 'live',
  Scheduled: 'scheduled',
};

/** The three the raw status is authoritative for, whatever else the row says. */
const VOIDED_OR_RECORDED = new Set(['Cancelled', 'LateCancelled', 'NoShow']);

/** The status word for one occurrence, or `null` where the server said something unreadable. */
export function occurrenceStatusOf(source: OccurrenceStatusSource): OccurrenceStatusKey | null {
  const status = source.status?.trim() ?? '';
  if (VOIDED_OR_RECORDED.has(status)) return BY_STATUS[status] ?? null;

  const derived = source.derivedStatus?.trim() ?? '';
  if (derived !== '') return BY_DERIVED[derived] ?? null;

  return status === '' ? null : (BY_STATUS[status] ?? null);
}

/**
 * The statuses that count as work the client OWED and did not do.
 *
 * One member today. `noShow` is deliberately outside it: R21 keeps a no-show in the adherence
 * denominator, and it is a different fact about a different kind of appointment — a surface that
 * wants both says both.
 */
const UNPERFORMED: ReadonlySet<OccurrenceStatusKey> = new Set<OccurrenceStatusKey>(['missed']);

/** Did the client finish this occurrence? */
export function occurrenceWasDone(key: OccurrenceStatusKey | null): boolean {
  return key === 'completed';
}

/** Was this occurrence owed and not trained? */
export function occurrenceWasMissed(key: OccurrenceStatusKey | null): boolean {
  return key !== null && UNPERFORMED.has(key);
}

/**
 * Does this occurrence stand in an attendance denominator?
 *
 * Scheduled work that has not happened yet, work that was done, and work that was owed and missed.
 * Withdrawn work does not count on either side — a cancellation, a late cancellation and a decline
 * are all removed from the prescription (self-paced-plan R15) — and neither does an empty slot,
 * which has no content to attend. A no-show does count, per R21.
 */
export function occurrenceIsScheduledWork(key: OccurrenceStatusKey | null): boolean {
  return key === 'completed' || key === 'missed' || key === 'noShow' || key === 'scheduled' || key === 'live';
}
